'use client'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useState, type FormEvent } from 'react'
import { authClient } from '../../lib/auth-client'
import { AuthShell, authStyles as s } from '../_components/auth/AuthShell'
import { VerifyEmailStep } from '../_components/auth/VerifyEmailStep'

export default function SignUpPage() {
  return (
    <Suspense fallback={<AuthShell title="Loading…">{null}</AuthShell>}>
      <SignUpFlow />
    </Suspense>
  )
}

function SignUpFlow() {
  const router = useRouter()
  const searchParams = useSearchParams()
  // Route through the post-login dispatcher (`/home`) which branches by
  // active-org type. Forward an explicit deep-link `next` when present.
  const next = searchParams.get('next')

  // Signup is two-step: collect credentials, then verify the emailed OTP
  // (required before the account can enter the app). We hold the password
  // across the step boundary so a post-verify sign-in fallback works even
  // when auto-sign-in-after-verification doesn't fire.
  const [step, setStep] = useState<'form' | 'verify'>('form')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  function goHome() {
    router.push(next ? `/home?next=${encodeURIComponent(next)}` : '/home')
  }

  if (step === 'verify') {
    return (
      <VerifyEmailStep
        email={email}
        password={password}
        onVerified={goHome}
        onBack={() => setStep('form')}
      />
    )
  }

  return (
    <CreateAccountStep
      name={name}
      email={email}
      password={password}
      setName={setName}
      setEmail={setEmail}
      setPassword={setPassword}
      onCreated={() => setStep('verify')}
    />
  )
}

function CreateAccountStep({
  name,
  email,
  password,
  setName,
  setEmail,
  setPassword,
  onCreated,
}: {
  name: string
  email: string
  password: string
  setName: (v: string) => void
  setEmail: (v: string) => void
  setPassword: (v: string) => void
  onCreated: () => void
}) {
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirmMismatch = confirm.length > 0 && confirm !== password
  const confirmMatch = confirm.length > 0 && confirm === password

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    setError(null)
    const result = await authClient.signUp.email({ email, password, name })
    setSubmitting(false)
    if (result.error) {
      setError(result.error.message ?? 'Sign up failed.')
      return
    }
    // Account created; a 6-digit verification code was emailed. Move to
    // the verify step (email + password are lifted into the parent).
    onCreated()
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start tinkering with your HDB home in true 3D — free to design, no contractor required."
      footer={
        <p className={s.foot}>
          Already have an account? <Link href="/sign-in">Sign in</Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className={s.form}>
        <div className={s.field}>
          <label className={s.label} htmlFor="su-name">
            Name
          </label>
          <input
            id="su-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
            placeholder="Your name"
            className={s.input}
          />
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="su-email">
            Email
          </label>
          <input
            id="su-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={s.input}
          />
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="su-password">
            Password
          </label>
          <div className={s.inputWrap}>
            <input
              id="su-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              className={s.input}
            />
            <button
              type="button"
              className={s.toggle}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="su-confirm">
            Confirm password
          </label>
          <div className={s.inputWrap}>
            <input
              id="su-confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              className={s.input}
              aria-invalid={confirmMismatch}
            />
          </div>
          {confirmMismatch ? (
            <span className={`${s.hint} ${s.hintBad}`}>Passwords don&apos;t match.</span>
          ) : confirmMatch ? (
            <span className={`${s.hint} ${s.hintOk}`}>Passwords match.</span>
          ) : null}
        </div>
        {error ? <p className={s.error}>{error}</p> : null}
        <button type="submit" disabled={submitting} className={s.button}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthShell>
  )
}
